import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaCheckCircle, FaTimesCircle, FaTrash } from "react-icons/fa";
import { getAllUsers, deleteUser } from "../api/apiServices";

export default function RidersPage() {
  const [riders, setRiders] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [showDelete, setShowDelete] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const ITEMS_PER_PAGE = 20;

  const fetchRiders = async () => {
    try {
      setLoading(true);
      const res = await getAllUsers({
        page,
        limit: ITEMS_PER_PAGE,
        role: "rider",
      });
      setRiders(res.users || []);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiders();
  }, [page]);

  const filteredRiders = riders.filter((v) => {
    const q = search.toLowerCase();
    return (
      v.userName?.toLowerCase().includes(q) ||
      v.email?.toLowerCase().includes(q) ||
      v.phoneNumber?.includes(q) ||
      v.location?.city?.toLowerCase().includes(q)
    );
  });

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteUser(selectedRider.userId);
      setShowDelete(false);
      setSelectedRider(null);
      fetchRiders();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Hero */}
      <div className="relative h-[300px] bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center text-white">
        <h1 className="text-4xl font-extrabold">All Riders</h1>
      </div>

      {/* Summary */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-6xl mx-auto px-4 mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="p-6 bg-indigo-600 text-white rounded-xl">
          <p>Total Riders</p>
          <h3 className="text-3xl font-bold">{filteredRiders.length}</h3>
        </div>

        <div className="p-6 bg-green-600 text-white rounded-xl">
          <p>KYC Verified</p>
          <h3 className="text-3xl font-bold">
            {filteredRiders.filter((v) => v.kycVerified).length}
          </h3>
        </div>

        <div className="p-6 bg-purple-600 text-white rounded-xl">
          <p>Active Riders</p>
          <h3 className="text-3xl font-bold">
            {filteredRiders.filter((v) => v.status === "online").length}
          </h3>
        </div>
      </motion.div>

      {/* List */}
      <div className="max-w-6xl mx-auto px-4 mt-12">
        <input
          type="text"
          placeholder="Search rider..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-1/3 mb-6 px-4 py-2 border rounded-lg"
        />

        {loading ? (
          <p>Loading riders...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRiders.map((rider) => (
              <div
                key={rider.userId}
                className="bg-white p-5 rounded-xl shadow relative"
              >
                <button
                  onClick={() => {
                    setSelectedRider(rider);
                    setShowDelete(true);
                  }}
                  className="absolute top-3 right-3 text-red-500 hover:text-red-700"
                >
                  <FaTrash />
                </button>

                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={rider.avatar}
                    alt={rider.userName}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <p className="font-bold">{rider.userName}</p>
                    <p className="text-xs text-gray-500">{rider.email}</p>
                  </div>
                </div>

                <p>📞 {rider?.phoneNumber}</p>
                <p>📍 {rider?.location?.city || "—"}</p>

                <div className="flex justify-between mt-4">
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${
                      rider.kycVerified
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {rider.kycVerified ? "KYC Verified" : "KYC Pending"}
                  </span>

                  {rider.kycVerified ? (
                    <FaCheckCircle className="text-green-500" />
                  ) : (
                    <FaTimesCircle className="text-red-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-center gap-4 mt-10">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            Previous
          </button>

          <span>
            Page {page} of {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-2">Delete Rider</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                {selectedRider?.userName}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDelete(false)}
                className="px-4 py-2 border rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
